import React, { forwardRef, useCallback, useMemo, useRef, useState, useEffect, useImperativeHandle } from 'react';
import { View, Text, TouchableOpacity, Dimensions } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import BaseHierarchy, {
  BaseHierarchyHandle,
  HierarchyItem,
  HierarchyPosition,
  HierarchyLink
} from '@components/Hierarchy/BaseHierarchy';
import DraggableStageCard from './DraggableStageCard';
import ArrowView from '@navigation/screens/MidLevel/UsersScreen/utils/ArrowView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

// Helper function to calculate connection points
const getConnectionPoint = (
  position: HierarchyPosition,
  side: 'left' | 'right' | 'top' | 'bottom',
  componentWidth: number = 280,
  componentHeight: number = 180
) => {
  switch (side) {
    case 'left':
      return { x: position.x, y: position.y + componentHeight / 2 };
    case 'right':
      return { x: position.x + componentWidth, y: position.y + componentHeight / 2 };
    case 'top':
      return { x: position.x + componentWidth / 2, y: position.y };
    case 'bottom':
      return { x: position.x + componentWidth / 2, y: position.y + componentHeight };
    default:
      return { x: position.x + componentWidth / 2, y: position.y + componentHeight / 2 };
  }
};

// Link component that maintains its own state reading from refs
const MemoizedLink = ({
  link,
  stagePositionsRef,
  stageItemsRef,
  theme
}: {
  link: HierarchyLink;
  stagePositionsRef: React.RefObject<Record<string, HierarchyPosition>>;
  stageItemsRef?: React.RefObject<StageItem[]>;
  theme: boolean
}) => {
  const [sourcePos, setSourcePos] = useState(() => stagePositionsRef.current?.[link.sourceId]);
  const [targetPos, setTargetPos] = useState(() => stagePositionsRef.current?.[link.targetId]);

  useEffect(() => {
    const updatePositions = () => {
      const newSourcePos = stagePositionsRef.current?.[link.sourceId];
      const newTargetPos = stagePositionsRef.current?.[link.targetId];

      if (newSourcePos && (newSourcePos.x !== sourcePos?.x || newSourcePos.y !== sourcePos?.y)) {
        setSourcePos(newSourcePos);
      }

      if (newTargetPos && (newTargetPos.x !== targetPos?.x || newTargetPos.y !== targetPos?.y)) {
        setTargetPos(newTargetPos);
      }
    };

    updatePositions();
    const interval = setInterval(updatePositions, 16); // ~60fps

    return () => clearInterval(interval);
  }, [link.sourceId, link.targetId, stagePositionsRef, sourcePos, targetPos]);

  if (!sourcePos || !targetPos) return null;

  // Check if source/target is primary to use correct component dimensions
  const allStages = stageItemsRef?.current || [];
  const sourceStage = allStages.find(s => s?._id === link.sourceId);
  const targetStage = allStages.find(s => s?._id === link.targetId);

  const isPrimarySource = sourceStage?.type === 'primary';
  const isPrimaryTarget = targetStage?.type === 'primary';

  const sourceConnectionPoint = getConnectionPoint(
    sourcePos,
    link.sourceConnectionPoint || 'right',
    isPrimarySource ? 320 : 280,
    isPrimarySource ? 200 : 180
  );
  const targetConnectionPoint = getConnectionPoint(
    targetPos,
    link.targetConnectionPoint || 'left',
    isPrimaryTarget ? 320 : 280,
    isPrimaryTarget ? 200 : 180
  );

  // Calculate deltaY for debugging
  const deltaY = Math.abs(targetConnectionPoint.y - sourceConnectionPoint.y);
  const isHorizontal = deltaY < 2;



  // Determine if this is a conditional connection
  const isConditional = link.type === 'conditional' || (link as any).metadata?.isConditional;
  const conditionLabel = (link as any).metadata?.conditionLabel || '';

  return (
    <>
      <ArrowView
        showPlusCircle={false}
        from={{
          x: sourceConnectionPoint.x,
          y: sourceConnectionPoint.y
        }}
        to={{
          x: targetConnectionPoint.x,
          y: targetConnectionPoint.y
        }}
        startColor={
          isConditional
            ? (theme ? "#8b5cf6" : "#a78bfa")  // Purple for conditional - lighter in dark mode
            : link.type === 'sequential'
              ? (theme ? "#3b82f6" : "#60a5fa")  // Blue - lighter in dark mode
              : (theme ? "#f59e0b" : "#fbbf24")  // Orange - lighter in dark mode
        }
        endColor={
          isConditional
            ? (theme ? "#a78bfa" : "#c4b5fd")  // Light purple for conditional - even lighter in dark mode
            : link.type === 'sequential'
              ? (theme ? "#60a5fa" : "#93c5fd")  // Light blue - lighter in dark mode
              : (theme ? "#fbbf24" : "#fde047")  // Light orange - lighter in dark mode
        }
        width={link.width || (isConditional ? 2 : 3)}
        gap={20}
        curveDelta={50}
        data={link}
        showAvs={false}
        text={conditionLabel}
        animate={false}
        debug={isHorizontal}
        pressCallback={() => {
          console.log('🔗 Stage connection clicked:', link);
        }}
      />

      {/* Condition Label for conditional connections */}
      {isConditional && conditionLabel && (
        <View
          style={{
            position: 'absolute',
            left: (sourceConnectionPoint.x + targetConnectionPoint.x) / 2 - 50,
            top: (sourceConnectionPoint.y + targetConnectionPoint.y) / 2 - 15,
            backgroundColor: theme ? '#f3e8ff' : '#4c1d95',
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: theme ? '#8b5cf6' : '#a78bfa'
          }}
        >
          <Text style={{
            fontSize: 10,
            fontWeight: '600',
            color: theme ? '#6d28d9' : '#c4b5fd'
          }}>
            {conditionLabel}
          </Text>
        </View>
      )}
    </>
  );
};

export interface StageItem extends HierarchyItem {
  _id: string;
  title: string;
  description?: string;
  type: 'greeting' | 'introduction' | 'data_collection' | 'setup' | 'training' | 'completion' | 'diagnosis' | 'testing' | 'resolution' | 'action' | 'decision' | 'notification' | 'approval' | 'review' | 'verification' | 'custom' | 'primary';
  order: number;
  config?: {
    estimatedDuration?: number;
    required?: boolean;
    skippable?: boolean;
    repeatable?: boolean;
    maxRepetitions?: number;
  };
  dataCollection?: {
    fields?: Array<{
      name: string;
      type: string;
      required?: boolean;
      validation?: string;
      options?: string[];
      helpText?: string;
      example?: string;
      placeholder?: string;
      label?: string;
    }>;
    form?: string;
  };
  stageActions?: Array<{
    actionType: string;
    config?: any;
    triggerOn?: string;
    calendarConfig?: {
      calendarId?: string;
      duration?: number;
      buffer?: number;
      autoConfirm?: boolean;
    };
  }>;
  voiceConfig?: {
    aiPrompt?: string;
    expectedPhrases?: string[];
    navigationKeywords?: string[];
    provideContext?: boolean;
    ragEnhancement?: string;
  };
  verificationConfig?: {
    dataSchemaId?: string;
    fieldsToVerify?: string[];
    matchThreshold?: number;
    availableFields?: Array<{
      name: string;
      type: string;
      label: string;
    }>;
    onSuccess?: {
      actionType: string;
      config?: any;
    };
    onFailure?: {
      actionType: string;
      config?: any;
    };
  };
  conditions?: any;
  resources?: any;
  metadata?: any;
  connections?: { targetId: string; type: string }[];
  [key: string]: any;
}

interface FlowHierarchyProps {
  flowId: string;
  flowTitle: string;
  flowDescription?: string;
  initialStages: StageItem[];
  mode: 'edit' | 'view';
  onStagePositionChange?: (stageId: string, position: HierarchyPosition) => void;
  onStageConnectionChange?: (sourceId: string, targetId: string, type: string) => void;
  onStageEdit?: (stage: StageItem) => void;
  onStageDelete?: (stageId: string) => void;
  onAddSubStage?: (parentStageId: string) => void;
}

export interface FlowHierarchyHandle extends BaseHierarchyHandle {
  addStage: (stage: StageItem) => void;
  removeStage: (stageId: string) => void;
  updateStage: (stageId: string, updates: Partial<StageItem>) => void;
  getStagesWithPositionsAndConnections: () => StageItem[];
}

const FlowHierarchy = forwardRef<FlowHierarchyHandle, FlowHierarchyProps>((props, ref) => {
  const {
    flowId,
    flowTitle,
    flowDescription,
    initialStages,
    mode,
    onStagePositionChange,
    onStageConnectionChange,
    onStageEdit,
    onStageDelete,
    onAddSubStage
  } = props;

  const theme = themeSelector();

  const baseHierarchyRef = useRef<BaseHierarchyHandle>(null);
  const draggableStageRefs = useRef<Map<string, any>>(new Map());
  const stagePositionsRef = useRef<Record<string, HierarchyPosition>>({});
  const stageItemsRef = useRef<StageItem[]>([]);

  const [stages, setStages] = useState<StageItem[]>(initialStages);
  const [stagePositions, setStagePositions] = useState<Record<string, HierarchyPosition>>({});
  const [connections, setConnections] = useState<Array<{ sourceId: string, targetId: string, type: string }>>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState<{ stageId: string; side: 'top' | 'right' | 'bottom' | 'left' } | null>(null);

  // Update stages when initialStages prop changes
  useEffect(() => {
    setStages(initialStages);

    // Also load connections from stage data (if any saved connections exist)
    const loadedConnections: { sourceId: string; targetId: string; type: string; }[] = [];
    initialStages.forEach((stage) => {
      if (stage.connections) {
        stage.connections.forEach(conn => {
          if (stage._id && conn.targetId) {
            loadedConnections.push({
              sourceId: stage._id,
              targetId: conn.targetId,
              type: conn.type
            });
          }
        });
      }
    });

    // Set connections (empty array if no saved connections, which allows auto-generation)
    setConnections(loadedConnections);
  }, [initialStages]);

  // Initialize stagePositionsRef and stagePositions state with initial positions
  useEffect(() => {
    const initialPositions: Record<string, HierarchyPosition> = {};

    initialStages.forEach(stage => {
      if (stage.x !== undefined && stage.y !== undefined) {
        const position = { x: stage.x, y: stage.y };
        stagePositionsRef.current[stage._id] = position;
        initialPositions[stage._id] = position;
      }
    });

    // Initialize the primary flow position (left-aligned for horizontal flow)
    if (flowId) {
      const componentWidth = 280;
      const componentHeight = 180;
      const verticalSpacing = componentHeight + 80;

      // Calculate how many stages are in the first column
      const stagesInFirstColumn = Math.min(5, initialStages.length);

      // Calculate the Y position of the first stage in the first column
      const firstStageY = 2000; // Start from center of viewport

      // Calculate the Y position of the last stage in the first column
      const lastStageY = firstStageY + ((stagesInFirstColumn - 1) * verticalSpacing);

      // Position primary flow on the left with vertical centering
      const primaryPosition = {
        x: 200, // Left margin for horizontal flow
        y: (firstStageY + lastStageY) / 2 - (componentHeight / 2)
      };
      stagePositionsRef.current[flowId] = primaryPosition;
      initialPositions[flowId] = primaryPosition;
    }

    setStagePositions(initialPositions);
  }, [initialStages, flowId]);

  // Create primary flow item
  const primaryFlowItem: StageItem | undefined = useMemo(() => {
    const componentWidth = 280;
    const componentHeight = 180;
    const verticalSpacing = componentHeight + 80;

    // Calculate how many stages are in the first column
    const stagesInFirstColumn = Math.min(5, initialStages.length);

    // Calculate the Y position of the first stage in the first column
    const firstStageY = 2000; // Start from center of viewport

    // Calculate the Y position of the last stage in the first column
    const lastStageY = firstStageY + ((stagesInFirstColumn - 1) * verticalSpacing);

    // Position primary flow on the left with vertical centering (horizontal flow layout)
    const baseX = 200; // Left margin for horizontal flow
    const baseY = (firstStageY + lastStageY) / 2 - (componentHeight / 2);

    return {
      _id: flowId || 'primary-flow',
      title: flowTitle || 'Main Flow',
      description: flowDescription || 'This is the main flow overview and configuration.',
      type: 'primary' as any,
      order: -1,
      config: {
        estimatedDuration: 0,
        required: true,
        skippable: false,
        repeatable: false,
        maxRepetitions: 1
      },
      x: baseX,
      y: baseY,
    };
  }, [flowId, flowTitle, flowDescription, initialStages.length]);

  // Convert stages to StageItem format with smart positioning
  const stageItems: StageItem[] = useMemo(() => {
    if (!stages || stages.length === 0) return [];

    // Filter out any null/undefined stages before processing
    const validStages = stages.filter(stage => stage != null && stage._id != null);

    return validStages.map((stage, index) => {
      let calculatedPosition = { x: 0, y: 0 };

      // Check if this is a sub-stage
      const isSubStage = stage?.metadata?.isSubStage || stage?._id?.startsWith('substage_');
      const parentStageId = stage?.metadata?.parentStageId;

      if (isSubStage && parentStageId) {
        // Position sub-stage relative to parent
        const parentStage = validStages.find(s => s?._id === parentStageId);
        if (parentStage && parentStage.x !== undefined && parentStage.y !== undefined) {
          calculatedPosition = {
            x: parentStage.x + 320, // 320px to the right
            y: parentStage.y + 40   // 40px down for offset
          };
        }
      } else if (primaryFlowItem) {
        // Regular stage positioning - horizontal flow layout
        const baseX = primaryFlowItem.x || 200; // Start from left margin
        const firstStageY = 2000; // Start from center of viewport

        const componentWidth = 280;
        const componentHeight = 180;
        const horizontalSpacing = componentWidth + 150; // Space between columns
        const verticalSpacing = componentHeight + 80; // Space between stages in a column

        // Only count non-sub-stages for level calculation
        const regularStagesBeforeThis = validStages.slice(0, index).filter(
          s => s != null && !s?.metadata?.isSubStage && !s?._id?.startsWith('substage_')
        ).length;

        // Column 0 is reserved for the base flow component, so stages start at column 1
        const column = Math.floor(regularStagesBeforeThis / 5) + 1; // 5 stages per column
        const rowInColumn = regularStagesBeforeThis % 5;

        const stageX = baseX + (column * horizontalSpacing);
        const stageY = firstStageY + (rowInColumn * verticalSpacing);

        calculatedPosition = { x: stageX, y: stageY };
      } else {
        const cols = Math.ceil(Math.sqrt(validStages.length));
        const row = Math.floor(index / cols);
        const col = index % cols;
        calculatedPosition = {
          x: col * 380,
          y: row * 480
        };
      }

      return {
        ...stage,
        x: stage.x !== undefined ? stage.x : calculatedPosition.x,
        y: stage.y !== undefined ? stage.y : calculatedPosition.y,
      };
    });
  }, [stages, primaryFlowItem]);

  // Combine all items for BaseHierarchy
  const allHierarchyItems: StageItem[] = useMemo(() => {
    const items: StageItem[] = [];

    if (primaryFlowItem) {
      items.push(primaryFlowItem);
    }

    items.push(...stageItems);

    // Update ref for MemoizedLink to access
    stageItemsRef.current = items;

    return items;
  }, [primaryFlowItem, stageItems]);


  // Generate links between stages
  const stageLinks: HierarchyLink[] = useMemo(() => {
    const links: HierarchyLink[] = [];

    // Add user-created connections
    connections.forEach(connection => {
      // Skip if either ID is missing
      if (!connection.sourceId || !connection.targetId) return;

      // Don't check positions here - just create the link
      // Positions are checked by the link rendering logic
      links.push({
        sourceId: connection.sourceId,
        targetId: connection.targetId,
        type: connection.type,
        width: 3
      });
    });

    // Add automatic links for sequential flow
    if (primaryFlowItem && stageItems.length > 0) {
      // Sort stages by order to create proper sequential flow
      const sortedStages = [...stageItems]
        .filter(s => !s.metadata?.isSubStage && !s._id?.startsWith('substage_'))
        .sort((a, b) => a.order - b.order);

      // Connect primary flow ONLY to the first stage (lowest order)
      if (sortedStages.length > 0 && primaryFlowItem._id && sortedStages[0]._id) {
        const firstStage = sortedStages[0];
        const isAlreadyConnectedToPrimary = connections.some(
          conn => conn.sourceId === primaryFlowItem._id && conn.targetId === firstStage._id
        );

        if (!isAlreadyConnectedToPrimary) {
          links.push({
            sourceId: primaryFlowItem._id,
            targetId: firstStage._id,
            type: 'sequential',
            width: 3,
            sourceConnectionPoint: 'right',
            targetConnectionPoint: 'left'
          });
        }
      }

      // Add horizontal sequential links between stages based on order
      sortedStages.forEach((stage, index) => {
        if (index < sortedStages.length - 1) {
          const nextStage = sortedStages[index + 1];

          // Check if connection already exists (in user connections or links array)
          const alreadyConnected =
            links.some(l => l.sourceId === stage._id && l.targetId === nextStage._id) ||
            connections.some(c => c.sourceId === stage._id && c.targetId === nextStage._id);

          if (!alreadyConnected && stage._id && nextStage._id) {
            links.push({
              sourceId: stage._id,
              targetId: nextStage._id,
              type: 'sequential',
              width: 3,
              sourceConnectionPoint: 'right',
              targetConnectionPoint: 'left'
            });
          }
        }
      });

      // Add conditional routing links based on stage conditions
      stageItems.forEach((stage) => {
        if (stage.conditions?.nextStageRules && stage.conditions.nextStageRules.length > 0) {
          stage.conditions.nextStageRules.forEach((rule: any) => {
            const targetStageIndex = rule.nextStageIndex;
            const targetStageId = rule.nextStageId;

            // Find target stage either by index or ID
            let targetStage: StageItem | undefined;
            if (targetStageIndex !== undefined) {
              // Filter main stages (not sub-stages) and get by index
              const allMainStages = stageItems.filter(s => !s.metadata?.isSubStage);
              targetStage = allMainStages[targetStageIndex];
            } else if (targetStageId) {
              targetStage = stageItems.find(s => s._id === targetStageId);
            }

            if (targetStage && stage._id && targetStage._id) {
              // Check if conditional link already exists
              const conditionalLinkExists = links.some(
                l => l.sourceId === stage._id &&
                  l.targetId === targetStage._id &&
                  (l.type === 'conditional' || (l as any).metadata?.isConditional)
              );

              if (!conditionalLinkExists) {
                // Create condition label for display
                const condition = rule.condition || {};
                const conditionLabel = condition.field
                  ? `IF ${condition.field} ${condition.operator} ${condition.value}`
                  : 'Conditional';

                links.push({
                  sourceId: stage._id,
                  targetId: targetStage._id,
                  type: 'conditional',
                  width: 2,
                  sourceConnectionPoint: 'bottom',
                  targetConnectionPoint: 'top',
                  ...(({
                    metadata: {
                      isConditional: true,
                      conditionLabel: conditionLabel,
                      condition: condition
                    }
                  } as any))
                });
              }
            }
          });
        }
      });

      // Handle sub-stages separately
      stageItems.forEach((stage) => {
        const isSubStage = stage.metadata?.isSubStage || stage._id?.startsWith('substage_');
        const parentStageId = stage.metadata?.parentStageId;

        if (isSubStage && parentStageId && stage._id) {
          // Connect sub-stage to its parent
          const parentAlreadyConnected = links.some(
            l => l.sourceId === parentStageId && l.targetId === stage._id
          );
          if (!parentAlreadyConnected) {
            links.push({
              sourceId: parentStageId,
              targetId: stage._id,
              type: 'has_substage',
              width: 2,
              sourceConnectionPoint: 'right',
              targetConnectionPoint: 'left'
            });
          }
        }
      });
    }

    return links;
  }, [connections, primaryFlowItem, stageItems]);

  // Handle stage position changes (on drop, updates state)
  const handleStagePositionChange = useCallback((stageId: string, position: HierarchyPosition) => {
    stagePositionsRef.current[stageId] = position;

    setStagePositions(prev => ({
      ...prev,
      [stageId]: position
    }));

    onStagePositionChange?.(stageId, position);
  }, [onStagePositionChange]);

  // Handle continuous position updates during drag (only updates refs)
  const handleStagePositionUpdate = useCallback((stageId: string, position: HierarchyPosition) => {
    stagePositionsRef.current[stageId] = position;
  }, []);

  // Render function for stage items
  const renderStageItem = useCallback((
    stage: StageItem,
    position: HierarchyPosition,
    onPositionChange: (position: HierarchyPosition) => void,
    zoomLevel?: number
  ) => {
    return (
      <DraggableStageCard
        ref={(ref) => {
          if (ref) {
            draggableStageRefs.current.set(stage._id, ref);
          } else {
            draggableStageRefs.current.delete(stage._id);
          }
        }}
        stage={stage}
        initialPosition={position}
        onPositionChange={onPositionChange}
        onPositionUpdate={(position: HierarchyPosition) => {
          handleStagePositionUpdate(stage._id, position);
        }}
        dragEnabled={mode === 'edit'}
        isEditMode={mode === 'edit'}
        isPrimary={stage.type === 'primary'}
        onEditStage={(stageId: string) => {
          const stage = stages.find(s => s._id === stageId);
          if (stage && onStageEdit) {
            onStageEdit(stage);
          }
        }}
        onDeleteStage={(stageId: string) => onStageDelete?.(stageId)}
        onAddSubStage={(parentStageId: string) => {
          console.log('🎯 onAddSubStage called in hierarchy:', { parentStageId, handler: !!onAddSubStage });
          if (onAddSubStage) {
            onAddSubStage(parentStageId);
          } else {
            console.error('❌ onAddSubStage is not defined');
          }
        }}
        onSnap={(targetId, sourceId) => {
          setConnections(prev => [...prev, {
            sourceId,
            targetId,
            type: 'custom',
            sourceConnectionPoint: 'right',
            targetConnectionPoint: 'left'
          }]);
        }}
        getAllStagePositions={() => stagePositions}
        zoomLevel={zoomLevel}
      />
    );
  }, [mode, onStageEdit, onStageDelete, stagePositions, stages, handleStagePositionUpdate]);

  // Stage management functions
  const addStage = useCallback((newStage: StageItem) => {
    console.log('➕ Adding stage:', newStage.title);
    setStages(prev => [...prev, newStage]);
  }, []);

  const removeStage = useCallback((stageId: string) => {
    if (!stageId) {
      console.error('Cannot remove stage: stageId is undefined');
      return;
    }
    console.log('➖ Removing stage:', stageId);
    setStages(prev => prev.filter(s => s?._id !== stageId));
    setConnections(prev => prev.filter(conn => conn?.sourceId !== stageId && conn?.targetId !== stageId));
  }, []);

  const updateStage = useCallback((stageId: string, updates: Partial<StageItem>) => {
    if (!stageId) {
      console.error('Cannot update stage: stageId is undefined');
      return;
    }
    console.log('✏️ Updating stage:', stageId);
    setStages(prev => prev.map(s => (s?._id === stageId && s != null) ? { ...s, ...updates } : s));
  }, []);

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    ...baseHierarchyRef.current!,
    addStage,
    removeStage,
    updateStage,
    getStagesWithPositionsAndConnections: () => {
      return stages.filter(stage => stage != null && stage._id != null).map(stage => ({
        ...stage,
        position: stagePositions[stage._id] || { x: stage.x, y: stage.y },
        connections: connections.filter(conn => conn?.sourceId === stage._id).map(conn => ({
          targetId: conn?.targetId,
          type: conn?.type
        }))
      }));
    }
  }));

  // Calculate initial viewport offset to show left side of horizontal flow
  const screenHeight = Dimensions.get('window').height;
  const viewportWidth = 20000; // Increased from 4000 for much larger canvas
  const viewportHeight = 20000;
  const initialOffset = {
    x: -100, // Show left side with small margin
    y: -((viewportHeight / 2) - (screenHeight / 2)) // Keep vertical centering at 10000
  };

  return (
    <View style={{ flex: 1, position: 'relative' }}>
      <BaseHierarchy
        ref={baseHierarchyRef}
        items={allHierarchyItems}
        primaryItem={primaryFlowItem}
        links={stageLinks}
        isLoading={false}
        onItemPositionChange={handleStagePositionChange}
        renderItem={renderStageItem}
        renderLinkHandler={({ links, theme, zoomLevel }) => {
          return links.map((link, index) => {
            // Use index as backup if IDs are undefined to ensure unique keys
            const linkKey = `connection-${link.sourceId || 'unknown'}-${link.targetId || 'unknown'}-${index}`;
            return (
              <MemoizedLink
                key={linkKey}
                link={link}
                stagePositionsRef={stagePositionsRef}
                stageItemsRef={stageItemsRef}
                theme={theme}
              />
            );
          });
        }}
        viewportSize={{ width: 20000, height: 20000 }}
        gridSize={50}
        initialZoom={0.5}
        minZoom={0.1}
        maxZoom={3}
        initialViewportOffset={initialOffset} // Show left side of horizontal flow
        positioningStrategy="manual"
        centerSpacing={600}
        showGrid={true}
        showDebugInfo={__DEV__}
        backgroundColor={undefined}
      />

      {/* Connection Mode Instructions */}
      {isConnecting && mode === 'edit' && (
        <View style={{
          position: 'absolute',
          top: 20,
          left: 20,
          right: 20,
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.8)',
          padding: 16,
          borderRadius: 12,
          zIndex: 1000,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.3,
          shadowRadius: 8,
          elevation: 8
        }}>
          <Text style={{
            fontSize: 16,
            fontWeight: 'bold',
            color: theme ? '#374151' : '#f9fafb',
            textAlign: 'center',
            marginBottom: 8
          }}>
            🔗 Creating Stage Connection
          </Text>
          <Text style={{
            fontSize: 14,
            color: theme ? '#6b7280' : '#d1d5db',
            textAlign: 'center'
          }}>
            Click on a connection point to complete the connection, or press ESC to cancel.
          </Text>
        </View>
      )}
    </View>
  );
});

FlowHierarchy.displayName = 'FlowHierarchy';

export default FlowHierarchy;

